// Import Packages
import React, { useEffect, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from "recoil";
import HTMLReactParser from "html-react-parser";
import { useTranslation } from 'react-i18next';

// Import Material UI
import { Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Autocomplete } from "@material-ui/lab";
import { TextField } from "@material-ui/core";
import { makeStyles } from '@material-ui/core/styles';

// Import Pages
import { TextInput } from '../../../FlowConfigurationInputs/TextInput/NewTextInput';
import BoolSelect from '../../../FlowConfigurationInputs/BoolSelect/BoolSelect'
import VariablePicker from '../../../FlowConfigurationInputs/VariablePicker/VariablePicker';
import CustomDateTab from '../../../FlowConfigurationInputs/CustomDateTab/CustomDateTab';
import FieldLabel from '../../../FlowConfigurationInputs/FieldLabel/FieldLabel';

import {
    alertState,
    initialActions,
    saveAvailable,
    changedFields
} from "../../../../recoil-store/recoil-state";

// Import Services
import AutomService from '../../../../services/AutomService/AutomService';

// Import Icons
import APIIcon from "../../../../assets/icons/triggerIcons/API";
import ScheduleIcon from "../../../../assets/icons/triggerIcons/Schedule";
import WebhookIcon from "../../../../assets/icons/triggerIcons/Webhook";
import EmailIcon from "../../../../assets/icons/triggerIcons/Email";
import StandardIcon from "../../../../assets/icons/triggerIcons/Standard-Autom";
import RefreshIcon from '../../../../assets/icons/headerIcons/refresh-icon.svg';


// Import CSS
import './CallAutom.css';
import { CheckIfFlowUpdated } from "../../../../helpers/helper";


const useStyles = makeStyles({
    option: {
        '& > span': {
            marginRight: 10,
            fontSize: 12,
        },
        fontSize: 13,
        fontWeight: 600,
        color: '#46474E',
    }
});

const CallAutom = ({
    action,
    variables,
    setOpenVariable,
    variableRender,
    automId,
}) => {
    const [automList, setAutomList] = useState([]);
    const [selectedAutom, setSelectedAutom] = useState({});
    const [inputVariables, setInputVariables] = useState([]);
    const [outputVariables, setOutputVariables] = useState([]);

    const [outputVariableType, setOutputVariableType] = useState('string');
    const [hasOutput, setHasOutput] = useState(false);
    const [loading, setLoading] = useState(true);

    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const setChangeFields = useSetRecoilState(changedFields);
    const changedFieldsState = useRecoilValue(changedFields);

    const setAlert = useSetRecoilState(alertState);

    const { t } = useTranslation();
    const classes = useStyles();
    const label = "CONTENT.CONFIGURATION-FORM.AUTOM-MATE.CALL-AUTOM."

    useEffect(() => {
        if (action.actionId) {

            fetchData().then(r => r);
        }
    }, [action.item_id])

    const fetchData = async () => {
        setLoading(true);
        if (!action.fields.automId) {
            action.fields.automId = { value: "", isRequired: true }
        }

        const resp = await AutomService.getCallAutomList(automId);
        if (!resp) {
            return setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }));
        } else if (!resp.status) {
            return setAlert(alert => ({ ...alert, show: true, message: resp.message, severity: 'error' }));
        } else if (resp.code === 404) {
            action.fields = resetObject(action.fields);
            setAutomList([]);
            setSelectedAutom({});
            setOutputVariableType("string");
            setInputVariables([]);
            setOutputVariables([]);
            setHasOutput(false);
            setLoading(false);
            return setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.AUTOMS.AUTOM-NOT-FOUND-TO-CALL'), severity: 'info' }));
        }

        setAutomList(resp.data);
        if (action.fields.automId && action.fields.automId.value) {
            let s = resp.data.find(x => x.id === action.fields.automId.value);
            if (s) {
                setSelectedAutom(s);
                const resp = await AutomService.callAutomDetail(s.autom_version_id);
                if (!resp) {
                    setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }));
                } else if (!resp.status) {
                    setAlert(alert => ({ ...alert, show: true, message: resp.message, severity: 'error' }));
                }
                let input_variables = [];
                let output_variables = [];
                if (resp.data.variables) {
                    for (let v of resp.data.variables) {
                        if (v.is_input || !v.is_output) {
                            input_variables.push(v);
                        } else {
                            output_variables.push(v);
                        }
                    }
                }

                action.fields.input_variable_list.value = mergeVariable(action.fields.input_variable_list.value, input_variables)
                action.fields.output_variable_list.value = mergeVariable(action.fields.output_variable_list.value, output_variables)
                if (resp.data.outputType === 'noData') {
                    action.fields.hasOutput = { value: false }
                    if (action.fields.automOutput) {
                        delete action.fields.automOutput;
                    }
                } else {
                    let outputType = resp.data.outputType === 'application/json' ? "object" : "string"
                    if (!action.fields.automOutput) {
                        action.fields.automOutput = { value: "", type: "VARIABLEPICKER", variableType: outputType };
                    } else {
                        if (action.fields.automOutput.variableType !== outputType) {
                            action.fields.automOutput.value = "";
                        }
                        action.fields.automOutput.variableType = outputType;
                    }
                    action.fields.hasOutput = { value: true }
                }

                setOutputVariableType(action.fields.automOutput ? action.fields.automOutput.variableType : "string");
                setInputVariables(action.fields.input_variable_list.value);
                setOutputVariables(action.fields.output_variable_list.value);
                setHasOutput(action.fields.hasOutput.value);
            } else {
                action.fields = resetObject(action.fields);
                setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.AUTOMS.SELECTED-AUTOM-NOT-FOUND'), severity: 'error' }));
                setSelectedAutom({});
                setOutputVariableType("string");
                setInputVariables([]);
                setOutputVariables([]);
                setHasOutput(false);
            }
        }
        setLoading(false);
    }

    const resetObject = (obj) => {
        return {
            retryDuration: obj.retryDuration,
            numberOfRetries: obj.numberOfRetries,
            errorReaction: obj.errorReaction,
            automId: { value: '', isRequired: true }
        };
    };

    function customSort(a, b) {
        // If displayName is empty string or null, name will be used
        let nameA = a.display_name && a.display_name.trim() !== '' ? a.display_name : a.name;
        let nameB = b.display_name && b.display_name.trim() !== '' ? b.display_name : b.name;

        return nameA.localeCompare(nameB);
    }

    const mergeVariable = (existingArray, newArray) => {
        let updatedExistingArray = existingArray.reduce((acc, existingItem) => {
            const newItem = newArray.find(newItem => newItem.name === existingItem.name);
            if (newItem) {
                // Update existingItem with newItem
                acc.push(newItem);
            } else {
                if (action.fields[existingItem.name]) {
                    delete action.fields[existingItem.name];
                }
            }

            // If there is no newItem, delete this element
            return acc;
        }, []);

        // Add what is in newArray but not in existingArray
        newArray.forEach(newItem => {
            if (!existingArray.some(existingItem => existingItem.name === newItem.name)) {
                updatedExistingArray.push(newItem);
            }
        });
        return updatedExistingArray.sort(customSort);

    }

    const changeSelectedAutom = async (e, data) => {
        action.fields = resetObject(action.fields);

        const field = initialActionsData[action.id]?.fields["automId"];
        const oldValue = !field ? undefined : field.value;
        let v = data?.id ? data.id : "";
        CheckIfFlowUpdated("automId", action, oldValue, v, setSaveAvailable, setChangeFields, changedFieldsState);
        if (data) {
            action.fields.automId = { value: data.id, isRequired: true }
            setSelectedAutom(data);
            const resp = await AutomService.callAutomDetail(data.autom_version_id);
            if (!resp) {
                return setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }));
            } else if (!resp.status) {
                return setAlert(alert => ({ ...alert, show: true, message: resp.message, severity: 'error' }));
            }

            let input_variables = [];
            let output_variables = [];
            if (resp.data.variables) {
                for (let v of resp.data.variables) {
                    if (v.is_input || !v.is_output) {
                        input_variables.push(v);
                    } else {
                        output_variables.push(v);
                    }
                }
            }

            setInputVariables(input_variables);
            setOutputVariables(output_variables);
            action.fields.input_variable_list = { value: input_variables }
            action.fields.output_variable_list = { value: output_variables }
            if (resp.data.outputType === 'noData') {
                setHasOutput(false);
                action.fields.hasOutput = { value: false }
            } else {
                let outputType = resp.data.outputType === 'application/json' ? "object" : "string"
                setOutputVariableType(outputType);
                setHasOutput(true);
                action.fields.automOutput = { value: "", type: "VARIABLEPICKER", variableType: outputType };
                action.fields.hasOutput = { value: true }
            }

        } else {
            setSelectedAutom({});
            setInputVariables([]);
            setOutputVariables([]);
            setHasOutput(false);
            setOutputVariableType("string");
        }

    }

    const getIconForType = (integration_type, trigger_type, className) => {
        if (integration_type === 'TRIGGER' && trigger_type.includes('webhook')) {
            return <WebhookIcon className={className} />
        } else if (integration_type === 'TRIGGER' && trigger_type.includes('api')) {
            return <div className={className}><APIIcon /></div>
        } else if (integration_type === 'SCHEDULE' && trigger_type.includes('schedule')) {
            return <ScheduleIcon className={className} />
        } else if (integration_type === 'TRIGGER' && trigger_type.includes('email')) {
            return <div className={className}><EmailIcon /></div>
        } else {
            return <StandardIcon className={className} />
        }
    }

    const MyToolTip = ({ description }) => {
        // marginRight: 32, marginLeft: 5, position: "relative", top: 7 
        return (
            description && (
                <Tooltip title={HTMLReactParser(description)}>
                    <InfoIcon style={{ width: 13, height: 13, marginLeft: 3 }} />
                </Tooltip>
            )
        )
    }

    // select input field option
    const changeInputOption = (inputName, value) => {
        const field = initialActionsData[action.id]?.fields[inputName];
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated(inputName, action, oldValue, value, setSaveAvailable, setChangeFields, changedFieldsState);

        action.fields[inputName].value = value;
    }

    const getElementByType = (inputVariable) => {
        if (inputVariable?.type === 'date' || inputVariable?.type === 'time' || inputVariable?.type === 'datetime') {
            return <CustomDateTab action={action}
                displayName={inputVariable?.name}
                inputName={inputVariable?.name}
                type={inputVariable?.type}
                variableRender={variableRender}
                variables={variables}
                setOpenVariable={setOpenVariable}
                onChange={(v) => changeInputOption(inputVariable?.name, v)}
            />
        } else if (inputVariable?.type === 'boolean') {
            return <BoolSelect
                inputName={inputVariable?.name}
                variableRender={variableRender}
                variables={variables}
                action={action}
                displayName={inputVariable?.name}
                setOpenVariable={setOpenVariable}
                type={inputVariable?.type}
                onChange={changeInputOption}
                showTextTab={true}
            />
        } else {
            return <div> <TextInput inputName={inputVariable?.name}
                variableRender={variableRender}
                variables={variables}
                action={action}
                include={[inputVariable.type]}
                multiline={inputVariable?.type === "object"}
                setOpenVariable={setOpenVariable}
                fieldType={inputVariable?.type}
                displayName={inputVariable?.name}
                isRequired={inputVariable?.name === 'triggerParam'}
                hideRequired
            />
                <small className='conf-field-desc'><em>{t('CONTENT.CONFIGURATION-FORM.TEXT-VARIABLE-HELPER', { type: inputVariable.type })}</em></small>
            </div>
        }
    }

    return (
        loading ? <CircularProgress className='loading-circular-process' /> :
            <>
                <div className='action-selection-content'>
                    <div className="conf-field-container">
                        <FieldLabel name={t(label + "SELECT-AUTOM")} is_required />
                    </div>
                    <div className='items-center-between'>
                        <Autocomplete
                            value={selectedAutom}
                            onChange={(e, newValue) => changeSelectedAutom(e, newValue)}
                            id="call-autom-id"
                            style={{ width: 300, position: 'relative', background: '#FBFBFB' }}
                            size="small"
                            options={automList}
                            classes={{
                                option: classes.option,
                            }}
                            autoHighlight
                            getOptionLabel={(option) => option.autom_name}
                            getOptionSelected={(option, value) => option.id === value.id}
                            renderOption={(option) => (
                                <>
                                    <div className="optionContainer">
                                        <div className="iconContainer">
                                            {getIconForType(option.integration_type, option.trigger_type, "flex-element")}
                                        </div>
                                        <span className="optionText">{option.autom_name}</span>
                                    </div>

                                </>
                            )}
                            renderInput={(params) => (
                                <>
                                    {
                                        getIconForType(selectedAutom.integration_type, selectedAutom.trigger_type, "input-action-icon")
                                    }
                                    <TextField
                                        {...params}
                                        className="select-action-input"
                                        placeholder="Select an Autom"
                                        variant="outlined"
                                        classes={{
                                            root: classes.resize
                                        }}
                                        inputProps={{
                                            ...params.inputProps,
                                            "aria-autocomplete": 'new-password',
                                            autoComplete: 'new-password', // disable autocomplete and autofill
                                            form: {
                                                autocomplete: 'off',
                                            },
                                            style: {
                                                marginLeft: '36px',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#46474E'
                                            }
                                        }}
                                    />
                                </>
                            )}
                        />
                        <button className='cursor-pointer-button' onClick={() => fetchData()}>
                            <img src={RefreshIcon} alt="" />
                        </button>
                    </div>

                </div>
                {
                    selectedAutom?.id && <>
                        {

                            <div>
                                {inputVariables.length ? <h4>{t(label + "INPUT-VARIABLES")}</h4> : undefined}
                                <div className="custom-form-fields">
                                    {inputVariables.length ?
                                        inputVariables?.map((inputVariable, index) =>

                                            <div className="conf-field-container" key={index}>
                                                {/* <FieldLabel display_name={inputVariable?.display_name} name={inputVariable?.name} is_required={inputVariable?.name === 'triggerParam'} /> */}
                                                <div className="field-name-container">
                                                    <FieldLabel name={inputVariable?.display_name ? inputVariable.display_name : inputVariable.name} is_required={inputVariable?.name === 'triggerParam'} />
                                                    {/* {inputVariable?.display_name ? inputVariable.display_name : inputVariable.name} */}
                                                    {
                                                        inputVariable.description && inputVariable.description !== '<p><br></p>' && <MyToolTip description={inputVariable.description} />
                                                    }
                                                </div>
                                                {getElementByType(inputVariable)}
                                                {/* {inputVariable?.description && inputVariable?.description !== '<p><br></p>' &&
                                                    <small><em>{HTMLReactParser(inputVariable?.description)}</em></small>
                                                } */}
                                            </div>
                                        )
                                        :
                                        <div className='variable-info-message'>{t('CONTENT.CONFIGURATION-FORM.NO-CONFIGURABLE-INPUT-VARIABLE-FOUND')}</div>

                                    }

                                </div>
                            </div>

                        }

                        <div className='m-t-40' >
                            {hasOutput || outputVariables.length > 0 ? <h4>{t(label + "OUTPUT-VARIABLE")}</h4> : undefined}
                            <div className="custom-form-fields">

                                {hasOutput || outputVariables.length > 0 ?
                                    <div>
                                        {
                                            outputVariables.length > 0 &&
                                            outputVariables.map((v) =>
                                                <div className="conf-field-container">
                                                    <FieldLabel name={v.display_name ? v.display_name : v.name} />

                                                    <VariablePicker action={action} inputName={v.name}
                                                        variables={variables}
                                                        setOpenVariable={setOpenVariable}
                                                        styles={{ backgroundColor: "white" }}
                                                        filter={v.type}
                                                        isOutput={true}
                                                        hideRequired={true}
                                                    />

                                                    <small className='conf-field-desc'><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: v.type })}</em></small>

                                                </div>
                                            )

                                        }


                                        {
                                            hasOutput &&
                                            <div className="conf-field-container">
                                                <FieldLabel name={t(label + 'OUTPUT')} />

                                                <VariablePicker action={action} inputName="automOutput"
                                                    variables={variables}
                                                    setOpenVariable={setOpenVariable}
                                                    styles={{ backgroundColor: "white" }}
                                                    filter={outputVariableType}
                                                    isOutput={true}
                                                    hideRequired={true}
                                                />

                                                <small className='conf-field-desc'><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: outputVariableType })}</em></small>
                                            </div>
                                        }
                                    </div>
                                    :
                                    <div className='variable-info-message' >{t('CONTENT.CONFIGURATION-FORM.NO-CONFIGURABLE-OUTPUT-VARIABLE-FOUND')}</div>
                                }
                            </div>
                        </div>
                    </>
                }
            </>
    )
}

export default CallAutom
